import React, { useEffect, useState } from 'react';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import '@fontsource/nunito/800.css';
import '@fontsource/nunito/900.css';
import { fetchJson } from '../apiBase';
import LegalFooter from '../components/LegalFooter';
import { PARENT_MINI_TASK_STUDENT_HINT_KEY } from '../mini-tasks/parentMiniTaskSession';
import Header from './sections/Header';
import { useParentNavigate } from './ParentNavigation';
import './ParentStudentPageLayout.css';

export default function ParentStudentPageLayout({ studentId, studentFallback, pointBalance, activeAvatarId, starterEmoji, fetchPointBalance = false, activePage, students = [], onStudentChange, children }) {
  const parentNavigate = useParentNavigate();
  const [profile, setProfile] = useState(null);
  const [paymentStudentIds, setPaymentStudentIds] = useState([]);
  const [loadedBalance, setLoadedBalance] = useState(null);
  const [allowedStudents, setAllowedStudents] = useState([]);
  const isAdmin = window.location.pathname.startsWith('/admin/');
  useEffect(() => {
    if (isAdmin || activePage !== 'payments') return undefined;
    let active = true;
    fetchJson('/parent/allowed-students')
      .then(async (response) => response.ok ? response.json() : null)
      .then((body) => { if (active) setAllowedStudents(body?.items || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [activePage, isAdmin]);
  const selectableStudents = students.map((student) => ({
    ...student,
    ...allowedStudents.find((allowed) => String(allowed.student_id) === String(student.student_id)),
  }));

  useEffect(() => {
    if (!studentId) return undefined;
    let active = true;
    fetchJson(`/parent/overview?student_id=${encodeURIComponent(studentId)}`)
      .then(async (response) => response.ok ? response.json() : null)
      .then((body) => { if (active) setProfile({ studentId: String(studentId), student: body?.student || null }); })
      .catch(() => { if (active) setProfile({ studentId: String(studentId), student: null }); });
    return () => { active = false; };
  }, [studentId]);

  useEffect(() => {
    if (isAdmin) return undefined;
    let active = true;
    fetchJson('/parent/dandu-payments/students')
      .then(async (response) => response.ok ? response.json() : null)
      .then((body) => { if (active) setPaymentStudentIds((body?.items || []).map((item) => String(item.student_id))); })
      .catch(() => {});
    return () => { active = false; };
  }, [isAdmin]);

  useEffect(() => {
    if (!fetchPointBalance || !studentId || isAdmin) return undefined;
    let active = true;
    fetchJson(`/parent/points?student_id=${encodeURIComponent(studentId)}&limit=1`)
      .then(async (response) => response.ok ? response.json() : null)
      .then((body) => { if (active) setLoadedBalance({ studentId: String(studentId), value: body?.available_points ?? null }); })
      .catch(() => { if (active) setLoadedBalance(null); });
    return () => { active = false; };
  }, [fetchPointBalance, isAdmin, studentId]);

  const currentStudent = profile?.studentId === String(studentId) ? profile.student : null;
  const fallbackStudent = studentFallback ? {
    id: String(studentId || ''), name: studentFallback.first_name || '', surname: studentFallback.last_name || '',
    class_name: studentFallback.class_name || studentFallback.grade || '',
    avatar_id: studentFallback.avatar_id || null, starter_emoji: studentFallback.starter_emoji || null,
  } : null;
  const student = currentStudent || fallbackStudent;
  const shownStudent = student ? {
    ...student,
    avatar_id: activeAvatarId !== undefined ? activeAvatarId : student.avatar_id,
    starter_emoji: starterEmoji !== undefined ? starterEmoji : student.starter_emoji,
  } : null;
  const balance = pointBalance !== undefined ? pointBalance
    : loadedBalance?.studentId === String(studentId) ? loadedBalance.value : null;
  const idQuery = encodeURIComponent(studentId || '');
  const openMiniTasks = () => {
    if (!studentId) return;
    try { window.sessionStorage.setItem(PARENT_MINI_TASK_STUDENT_HINT_KEY, String(studentId)); } catch (_) { /* Session storage may be unavailable. */ }
    parentNavigate(`/mini-tasks/parent?student_id=${encodeURIComponent(studentId)}`);
  };

  return (
    <div className="parent-student-page">
      <div className="parent-student-shell">
        <Header overview={{ student: shownStudent }} onOpenMiniTasks={!isAdmin && studentId ? openMiniTasks : undefined}
          students={!isAdmin ? selectableStudents : []} studentId={studentId} onStudentChange={onStudentChange}
          avatarHref={!isAdmin && studentId ? `/avatarim?student_id=${idQuery}` : undefined}
          pointsHref={studentId ? `${isAdmin ? '/admin/puanlarim' : '/puanlarim'}?student_id=${idQuery}` : undefined}
          paymentsHref={!isAdmin && paymentStudentIds.includes(String(studentId)) ? `/odemelerim?student_id=${idQuery}` : undefined}
          pointBalance={balance} activePage={activePage}
          student360Href={`${isAdmin ? '/admin/student360' : '/student360'}?student_id=${idQuery}`} />
        <div className="parent-student-content">{children}</div>
        <LegalFooter variant="student" />
      </div>
    </div>
  );
}
